import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAssessment } from "../context/AssessmentContext";
import { RatingInput } from "../components/assessment/RatingInput";
import { VoiceInput } from "../components/assessment/VoiceInput";
import { ProgressBar } from "../components/assessment/ProgressBar";
import { CATEGORY_COLORS } from "../lib/utils";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  Home,
  HeartPulse,
  Brain,
  Users,
  Globe,
  Wallet,
  Briefcase,
  BookOpen,
  Sparkles,
  Check,
  Loader2,
} from "lucide-react";

const CATEGORY_ICONS = {
  physical: HeartPulse,
  emotional: Brain,
  family: Users,
  social: Globe,
  financial: Wallet,
  career: Briefcase,
  intellectual: BookOpen,
  spiritual: Sparkles,
};

export default function Assessment() {
  const navigate = useNavigate();
  const {
    user,
    member,
    session,
    categories,
    items,
    responses,
    progress,
    initializing,
    fetchAssessmentData,
    fetchResponses,
    fetchProgress,
    saveResponse,
    submitAssessment,
    clearSession,
  } = useAssessment();

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [localResponses, setLocalResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isRestoringData, setIsRestoringData] = useState(false);

  // Redirect if no session (after initialization completes)
  useEffect(() => {
    if (!initializing && (!member || !session)) {
      navigate("/");
    }
  }, [member, session, navigate, initializing]);

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      if (member && !dataLoaded) {
        setIsRestoringData(true);
        await fetchAssessmentData();
        await fetchResponses();
        await fetchProgress();
        setDataLoaded(true);
        // Small delay to allow state to settle before enabling auto-save
        setTimeout(() => setIsRestoringData(false), 500);
      }
    };
    loadData();
  }, [member, fetchAssessmentData, fetchResponses, fetchProgress, dataLoaded]);

  // Sync local responses with fetched responses - merge with existing local data
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      setLocalResponses((prev) => ({
        ...prev,
        ...responses,
      }));
    }
  }, [responses]);

  const currentCategory = categories[currentCategoryIndex];
  const categoryItems = currentCategory ? items[currentCategory.id] || [] : [];
  const currentItem = categoryItems[currentItemIndex];

  // Get response for current item - check both local and fetched responses
  const currentResponse = currentItem 
    ? (localResponses[currentItem.id] || responses[currentItem.id] || {}) 
    : {};

  // Auto-save when response changes (debounced) - skip during data restoration
  useEffect(() => {
    if (!currentItem || !localResponses[currentItem.id] || isRestoringData) return;
    
    const response = localResponses[currentItem.id];
    // Only auto-save if there's meaningful data
    if (response.rating === undefined && !response.q1_text && !response.q2_text) return;
    
    const timeoutId = setTimeout(async () => {
      setSaving(true);
      try {
        await saveResponse(
          currentItem.id,
          response.rating,
          response.q1_text,
          response.q2_text
        );
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setSaving(false);
      }
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(timeoutId);
  }, [currentItem, localResponses, saveResponse, isRestoringData]);

  // Update local response
  const updateResponse = useCallback((field, value) => {
    if (!currentItem) return;
    
    setLocalResponses((prev) => ({
      ...prev,
      [currentItem.id]: {
        ...prev[currentItem.id],
        [field]: value,
      },
    }));
  }, [currentItem]);

  // Save current item response (manual save)
  const saveCurrentResponse = useCallback(async () => {
    if (!currentItem) return;
    
    const response = localResponses[currentItem.id];
    if (!response) return;
    
    setSaving(true);
    try {
      await saveResponse(
        currentItem.id,
        response.rating,
        response.q1_text,
        response.q2_text
      );
    } catch (error) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [currentItem, localResponses, saveResponse]);

  // Navigate to next item
  const goToNext = useCallback(async () => {
    await saveCurrentResponse();
    
    if (currentItemIndex < categoryItems.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    } else if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
      setCurrentItemIndex(0);
    }
  }, [currentItemIndex, categoryItems.length, currentCategoryIndex, categories.length, saveCurrentResponse]);

  // Navigate to previous item
  const goToPrev = useCallback(async () => {
    await saveCurrentResponse();
    
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    } else if (currentCategoryIndex > 0) {
      const prevCategoryIndex = currentCategoryIndex - 1;
      const prevCategoryItems = items[categories[prevCategoryIndex].id] || [];
      setCurrentCategoryIndex(prevCategoryIndex);
      setCurrentItemIndex(prevCategoryItems.length - 1);
    }
  }, [currentItemIndex, currentCategoryIndex, categories, items, saveCurrentResponse]);

  // Jump to category
  const goToCategory = useCallback(async (categoryId) => {
    await saveCurrentResponse();
    
    const index = categories.findIndex((c) => c.id === categoryId);
    if (index !== -1) {
      setCurrentCategoryIndex(index);
      setCurrentItemIndex(0);
    }
  }, [categories, saveCurrentResponse]);

  // Handle submit
  const handleSubmit = async () => {
    await saveCurrentResponse();
    
    try {
      await submitAssessment();
      toast.success("Assessment submitted successfully!");
      navigate("/review");
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handle exit - save but don't clear session so user can return
  const handleExit = async () => {
    await saveCurrentResponse();
    toast.success("Progress saved! You can return anytime.");
    navigate("/");
  };

  // Check if on last item
  const isLastItem =
    currentCategoryIndex === categories.length - 1 &&
    currentItemIndex === categoryItems.length - 1;

  // Check if on first item
  const isFirstItem = currentCategoryIndex === 0 && currentItemIndex === 0;

  // Calculate total progress
  const totalItems = Object.values(items).flat().length;
  const completedItems = Object.values(localResponses).filter(
    (r) => r?.rating !== undefined && r?.rating !== null
  ).length;
  const progressPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  if (initializing || !currentCategory || !currentItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your assessment...</p>
        </div>
      </div>
    );
  }

  const CategoryIcon = CATEGORY_ICONS[currentCategory.id] || Sparkles;

  return (
    <div className="min-h-screen bg-background assessment-container" data-testid="assessment-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExitDialog(true)}
              data-testid="btn-exit"
            >
              <Home className="h-4 w-4 mr-2" />
              Exit
            </Button>
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground">{session?.name}</p>
              <p className="text-sm font-medium">{user?.display_name}</p>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/review")}
              data-testid="btn-review"
            >
              Review
            </Button>
          </div>
          
          <ProgressBar
            categories={categories}
            progress={progress}
            currentCategory={currentCategory?.id}
            onCategoryClick={goToCategory}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Category Header */}
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${CATEGORY_COLORS[currentCategory.id]}20` }}
          >
            <CategoryIcon
              className="h-6 w-6"
              style={{ color: CATEGORY_COLORS[currentCategory.id] }}
            />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{currentCategory.name}</h1>
            <p className="text-sm text-muted-foreground">
              Item {currentItemIndex + 1} of {categoryItems.length}
            </p>
          </div>
        </div>

        {/* Item Card */}
        <Card className="mb-6 border-0 shadow-lg" data-testid="item-card">
          <CardHeader
            className="pb-2"
            style={{ borderLeft: `4px solid ${CATEGORY_COLORS[currentCategory.id]}` }}
          >
            <CardTitle className="text-lg">{currentItem.name}</CardTitle>
            <CardDescription>{currentItem.description}</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Rating */}
            <div>
              <label className="text-sm font-medium mb-3 block">
                How would you rate this area? (1-10)
              </label>
              <RatingInput
                value={currentResponse.rating}
                onChange={(value) => updateResponse("rating", value)}
              />
            </div>

            {/* Q1: Why this rating */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Why did you give this rating?
                <span className="text-muted-foreground font-normal ml-1">(optional)</span>
              </label>
              <VoiceInput
                value={currentResponse.q1_text || ""}
                onChange={(value) => updateResponse("q1_text", value)}
                placeholder="Reflect on what influenced your rating..."
              />
            </div>

            {/* Q2: Commitment */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                What is one thing you commit to improving in the next 12 months?
                <span className="text-muted-foreground font-normal ml-1">(optional)</span>
              </label>
              <VoiceInput
                value={currentResponse.q2_text || ""}
                onChange={(value) => updateResponse("q2_text", value)}
                placeholder="Share a specific, actionable commitment..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={goToPrev}
            disabled={isFirstItem || saving}
            data-testid="btn-prev"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>

          {saving && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}

          {isLastItem ? (
            <Button
              onClick={() => setShowSubmitDialog(true)}
              disabled={saving}
              data-testid="btn-submit"
            >
              <Send className="h-4 w-4 mr-2" />
              Review & Submit
            </Button>
          ) : (
            <Button onClick={goToNext} disabled={saving} data-testid="btn-next">
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <div className="mobile-nav md:hidden">
        <div className="flex items-center justify-between px-2">
          <div className="text-xs text-muted-foreground">
            {completedItems}/{totalItems} complete
          </div>
          <Progress value={progressPercentage} className="w-32 h-2" />
          <div className="text-xs font-medium">{progressPercentage}%</div>
        </div>
      </div>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ready to Submit?</AlertDialogTitle>
            <AlertDialogDescription>
              You've completed {completedItems} of {totalItems} items ({progressPercentage}%).
              Once submitted, you won't be able to edit your responses.
              Would you like to review your answers first?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <Button variant="outline" onClick={() => {
              setShowSubmitDialog(false);
              navigate("/review");
            }}>
              Review First
            </Button>
            <AlertDialogAction onClick={handleSubmit}>
              Submit Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exit Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              Your progress has been saved. You can return anytime using the same invite code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Assessment</AlertDialogCancel>
            <AlertDialogAction onClick={handleExit}>
              Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
